/**
 * Minimal, dependency-free event primitive (架构方案 §5.15), modelled on the
 * VSCode `Emitter`/`Event` pattern. Composition over inheritance: anything that
 * needs to expose a subscribable change signal (e.g. a {@link ScopeLayer}'s
 * `onDidChange`, §5.12) holds an `Emitter` and exposes its `event`, then calls
 * `fire` when its underlying source changes — instead of hand-rolling a
 * listener `Set` each time.
 */

/** A disposer returned by subscribing; call it to unsubscribe. */
export type Disposer = () => void;

/** A subscriber invoked with each fired value. */
export type Listener<T> = (event: T) => void;

/** A subscribe function: register a listener, get a disposer back. */
export type Event<T> = (listener: Listener<T>) => Disposer;

export class Emitter<T> {
  private readonly listeners = new Set<Listener<T>>();

  /**
   * The subscribe function to expose to consumers (e.g. assign directly to a
   * `ScopeLayer.onDidChange`). Returns a disposer that removes the listener.
   */
  readonly event: Event<T> = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Deliver `value` to every current subscriber. */
  fire(value: T): void {
    // Snapshot so a listener that (un)subscribes during dispatch is safe.
    for (const listener of [...this.listeners]) {
      listener(value);
    }
  }

  /** Drop all subscribers (e.g. when the owner is disposed). */
  dispose(): void {
    this.listeners.clear();
  }
}
